import { ChartjsTooltip } from '../../../../components';
import { getSelectedLocationName, getSelectedLocations, getSelectedSubjectName, getSelectedSubjects, getUrlQueryParams, getSubjectColor, getAvgScoreForGroup, getGroupAveragesForSubjectForGraph, getGroupCodeFromName, getSubjectTitle, isCitvoiceMeasure, getSelectedYears, getSelectedGroupHighlight, isACountry, getLocationNameMarkup, getSpecificValue, roundToOne, calculateTrendNumbers, possibleException, isException } from '../../../../actions';
import { chartDataIterator } from '../RangeChart/utils';
import { regionCodes, defaultVariables, baseFont, trendClassificationColors } from '../../../../referenceData';
import i18n from '../../../../i18n';
import TitleWithNames from '../../../../components/NameTitle/index.jsx';
import getCountriesInGroup from '../../../../actions/getCountriesInGroup';

export const isMultipleSubjectSelected = (subjects) => subjects.length > 1;

const getAvarageValuesForSelectedYears = (data, selectedYears) => {
  if (typeof selectedYears === 'number') {
    selectedYears = [selectedYears];
  }
  const averageValues = [];
  data.values.forEach((value) => (selectedYears.includes(value.x) ? averageValues.push(value.y) : null));
  return averageValues;
};

const getAfricanAvarageStats = (state, subject, year) => {
  const avgData = chartDataIterator(state, getGroupAveragesForSubjectForGraph(state, getGroupCodeFromName(state, 'Africa'), subject), i18n.t('African average'), 'dashed', '#8e8e8e', false, false, 10, 1);

  return getAvarageValuesForSelectedYears(avgData, year);
};

const getDataForMultipleLocations = (state, subject, locations, year, groupHighlight, showClassification, highlightedTrend, selectedYearsRange1, selectedYearsRange2) => {
  const data = locations.map((location) => {
    const fullLocationname = getSelectedLocationName(state, location);
    const fullSubjectName = getSelectedSubjectName(state, subject);
    const subjectColor = getSubjectColor({ state, subject });
    let trendClassification = null;

    const countriesInGroup = [];
    if (groupHighlight) {
      countriesInGroup.push(...getCountriesInGroup(state, groupHighlight.uid).map((c) => c.iso));
    }

    if (!isACountry(location)) {
      return {
        key: fullLocationname,
        value: getAvgScoreForGroup(state, subject, year, location),
        props: {
          color: subjectColor,
          location: {
            fullName: fullLocationname,
            abr: location,
          },
          subject: {
            fullName: fullSubjectName,
            abr: subject,
          },
          trendClassification,
        },
      };
    }

    let colour = subjectColor;

    if (showClassification) {
      const startValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange1[0]));
      const endValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange1[selectedYearsRange1.length - 1]));
      const scoreChangeRange1 = endValue1 !== -1 && startValue1 !== -1 ? roundToOne(endValue1 - startValue1) : 'n/a';

      if (scoreChangeRange1 !== 'n/a') {
        const startValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[0]));
        const endValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[selectedYearsRange2.length - 1]));

        let exception = false;
        if (possibleException(endValue2)) {
          exception = isException(roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 3)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 2)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 1)), endValue2);
        }

        const results = calculateTrendNumbers(startValue1, endValue1, startValue2, endValue2, selectedYearsRange1, selectedYearsRange2, exception);
        trendClassification = results.classifText;
        colour = trendClassificationColors[results.classifIndex];
      }
    } else if (!showClassification && groupHighlight && !countriesInGroup.includes(location)) {
      colour = subjectColor + '44';
    }

    return {
      key: fullLocationname,
      value: Number(state.indexedYml[year][subject][location].v).toFixed(1),
      props: {
        color: colour,
        location: {
          fullName: fullLocationname,
          abr: location,
        },
        subject: {
          fullName: fullSubjectName,
          abr: subject,
        },
        trendClassification,
      },
    };
  });

  return data;
};

const getDataForMultipleSubjects = (state, subjects, location, year, showClassification, highlightedTrend, selectedYearsRange1, selectedYearsRange2) => {
  const data = subjects.map((subject) => {
    const fullSubjectName = getSelectedSubjectName(state, subject);
    const fullLocationname = getSelectedLocationName(state, location);
    let trendClassification = null;

    if (!isACountry(location)) {
      return {
        key: fullSubjectName,
        value: getAvgScoreForGroup(state, subject, year, location),
        props: {
          color: isCitvoiceMeasure(state, subject) ? '#A19FA3' : getSubjectColor({ state, subject }),
          location: {
            fullName: fullLocationname,
            abr: location,
          },
          subject: {
            fullName: fullSubjectName,
            abr: subject,
          },
          trendClassification,
        },
      };
    }

    let colour = isCitvoiceMeasure(state, subject) ? '#A19FA3' : getSubjectColor({ state, subject });

    if (showClassification) {
      const startValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange1[0]));
      const endValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange1[selectedYearsRange1.length - 1]));
      const scoreChangeRange1 = endValue1 !== -1 && startValue1 !== -1 ? roundToOne(endValue1 - startValue1) : 'n/a';

      if (scoreChangeRange1 !== 'n/a') {
        const startValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[0]));
        const endValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[selectedYearsRange2.length - 1]));

        let exception = false;
        if (possibleException(endValue2)) {
          exception = isException(roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 3)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 2)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 1)), endValue2);
        }

        const results = calculateTrendNumbers(startValue1, endValue1, startValue2, endValue2, selectedYearsRange1, selectedYearsRange2, exception);
        trendClassification = results.classifText;
        colour = trendClassificationColors[results.classifIndex];
      }
    }

    return {
      key: fullSubjectName,
      value: Number(state.indexedYml[year][subject][location].v).toFixed(1),
      props: {
        color: colour,
        location: {
          fullName: fullLocationname,
          abr: location,
        },
        subject: {
          fullName: fullSubjectName,
          abr: subject,
        },
        trendClassification,
      },
    };
  });
  return data;
};

const getChartHeader = (chartFilters, selectedYear) =>
  window.innerWidth < 480
    ? `${chartFilters} \n- ${i18n.t('Score/Rank')} ${selectedYear}` // eslint-disable-line i18next/no-literal-string
    : `${chartFilters} - ${i18n.t('Score/Rank')} ${selectedYear}`; // eslint-disable-line i18next/no-literal-string

const getDatasetsForChart = (state, subjects, locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange1, selectedYearsRange2) => {
  if (isMultipleSubjectSelected(subjects)) {
    return getDataForMultipleSubjects(state, subjects, locations[0], selectedYears, showClassification, highlightedTrend, selectedYearsRange1, selectedYearsRange2);
  }
  return getDataForMultipleLocations(state, subjects[0], locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange1, selectedYearsRange2);
};

const getDataForTooltip = (state, subjects, locations) => {
  const body = {};
  const footer = [];

  if (isMultipleSubjectSelected(subjects)) {
    const locationCode = locations[0];
    const location = getSelectedLocationName(state, locationCode);
    const locationMarkup = getLocationNameMarkup({ code: locationCode, name: location });
    subjects.forEach((subject) => {
      footer.push(i18n.t('African average for {{subject}}', { subject: getSelectedSubjectName(state, subject) }));
      body[getSelectedSubjectName(state, subject)] = {
        abr: subject,
        label: `${getSelectedSubjectName(state, subject)}: ${locationMarkup}`, // eslint-disable-line i18next/no-literal-string
        locationCode,
        locationName: location,
        locationMarkup,
      };
    });
  } else {
    footer.push(i18n.t('African average'));
    locations.forEach((location) => {
      const locationName = getSelectedLocationName(state, location);
      const locationMarkup = getLocationNameMarkup({ code: location, name: locationName });
      body[locationName] = {
        abr: subjects[0],
        label: `${getSelectedSubjectName(
          // eslint-disable-line i18next/no-literal-string
          state,
          subjects[0],
        )}: ${locationMarkup}`,
        locationCode: location,
        locationName,
        locationMarkup,
      };
    });
  }

  return {
    body,
    footer,
  };
};

export const getChartTitle = (state, urlParams) => {
  const years = getSelectedYears('range1from', 'range1to');
  const selectedYear = years.pop();
  const suffix = Number(selectedYear) !== defaultVariables.latest_year ? ` ${selectedYear}` : '';
  return <TitleWithNames subject={getSubjectTitle(state)} locations={getSelectedLocations(state)} suffix={suffix} />;
};

const sortData = (chartData, urlParams, measureTreeOrder) => {
  const { sortBy, sortDir, meas } = urlParams;
  let sortedData;

  if (typeof sortBy === 'undefined' || sortBy === 'score') {
    sortedData = [...chartData].sort((a, b) => Number(a.value) - Number(b.value));
  }

  if ((typeof sortBy !== 'undefined' && sortBy === 'index') || (sortBy === 'name' && isMultipleSubjectSelected(meas.split('-')))) {
    sortedData = [...chartData].sort((a, b) => measureTreeOrder.indexOf(a.props.subject.abr) - measureTreeOrder.indexOf(b.props.subject.abr)).reverse();
  }

  if (typeof sortBy !== 'undefined' && sortBy === 'name') {
    sortedData = [...chartData].sort((a, b) => a.key.localeCompare(b.key));
  }

  return typeof sortDir === 'undefined' || sortDir === 'des' ? sortedData.reverse() : sortedData;
};

const createBarChartDatasets = (state, isMultipleSubjectsSelected, data, year) => {
  if (isMultipleSubjectsSelected) {
    return {
      labels: data.map((subject) => subject.key),
      datasets: [
        {
          label: `${data[0].props.location.fullName}${
            Number(year) !== defaultVariables.latest_year ? ` (${year})` : '' // eslint-disable-line i18next/no-literal-string
          }`,
          backgroundColor: data.map((subject) => subject.props.color),
          hoverBackgroundColor: data.map((subject) => subject.props.color),
          trendClassifications: data.map((subject) => subject.props.trendClassification),
          data: data.map((subject) => {
            if (subject.value === '-1.0') {
              return NaN;
            }
            return subject.value;
          }),
          maxBarThickness: 150,
          tooltip: {
            order: 1,
          },
        },
        // ...data.map((subject) => ({
        //   label: `African average for ${subject.key}`,
        //   type: "line",
        //   isException: true,
        //   borderColor: "transparent",
        //   borderDash: [3, 3],
        //   pointRadius: 0,
        //   borderWidth: 1,
        //   data: Array(data.length).fill(
        //     ...getAfricanAvarageStats(state, subject.key, year)
        //   ),
        //   pointStyle: "none",
        //   tooltip: {
        //     divider: true,
        //     order: 11,
        //   },
        // })),
      ],
    };
  }
  if (data.length === 1) {
    const item = data[0];

    return {
      labels: ['', item.key, ''],
      datasets: [
        {
          label: item.props.subject.fullName,
          backgroundColor: ['transparent', item.props.color, 'transparent'],
          hoverBackgroundColor: ['transparent', item.props.color, 'transparent'],
          trendClassifications: [null, item.props.trendClassification, null],
          data: [item.value, item.value === '-1.0' ? NaN : item.value, item.value],
          maxBarThickness: 150,
          selectedYear: year,
          tooltip: {
            order: 1,
          },
        },
        {
          label: i18n.t('African average'),
          type: 'line',
          borderColor: '#9C9C9C',
          borderDash: [3, 3],
          borderWidth: 1,
          data: Array(3).fill(...getAfricanAvarageStats(state, data[0].props.subject.abr, year)),
          pointStyle: 'none',
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
          pointBackgroundColor: 'transparent',
        },
      ],
    };
  }
  return {
    labels: data.map((location) => location.key),
    datasets: [
      {
        label: data[0].props.subject.fullName,
        backgroundColor: data.map((el) => el.props.color),
        hoverBackgroundColor: data.map((el) => el.props.color),
        trendClassifications: data.map((el) => el.props.trendClassification),
        data: data.map((location) => {
          if (location.value === '-1.0') {
            return NaN;
          }
          return location.value;
        }),
        maxBarThickness: 150,
        selectedYear: year,
        tooltip: {
          order: 1,
        },
      },
      {
        label: i18n.t('African average'),
        type: 'line',
        borderColor: '#9C9C9C',
        borderDash: [3, 3],
        pointRadius: 0,
        borderWidth: 1,
        data: Array(data.length).fill(...getAfricanAvarageStats(state, data[0].props.subject.abr, year)),
        pointStyle: 'none',
        tooltip: {
          divider: true,
          order: 11,
        },
      },
    ],
  };
};

export const getChartConfig = (state) => {
  const urlParams = getUrlQueryParams();
  const subjects = getSelectedSubjects(state);
  const locations = getSelectedLocations(state);
  const years = getSelectedYears('range1from', 'range1to');
  const selectedYearsRange2 = getSelectedYears('range2from', 'range2to');
  const groupHighlight = getSelectedGroupHighlight(state);
  const selectedYear = years[years.length - 1];
  const tooltipData = getDataForTooltip(state, subjects, locations);
  const { measureTreeOrder } = state;
  const showClassification = JSON.parse(urlParams?.showClassifications || false);
  const highlightedTrend = urlParams?.highlightedTrend || null;

  const data = getDatasetsForChart(state, subjects, locations, selectedYear, groupHighlight, showClassification, highlightedTrend, years, selectedYearsRange2);

  const sortedData = sortData(data, urlParams, measureTreeOrder);

  const chartData = createBarChartDatasets(state, isMultipleSubjectSelected(subjects), sortedData, selectedYear);

  const indexAxis = window.innerWidth < 768 ? 'y' : 'x';
  const otherAxis = indexAxis === 'y' ? 'x' : 'y';

  return {
    type: 'bar',
    data: chartData,
    options: {
      interaction: {
        intersect: false,
        mode: window.innerWidth > 768 ? 'index' : indexAxis,
        includeInvisible: true,
      },
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          bottom: 35,
          top: 30,
        },
      },
      indexAxis,
      scales: {
        [indexAxis]: {
          title: {
            display: locations.length >= 40 || subjects.length >= 40,
            text: subjects.length === 1 ? i18n.t('Locations').toUpperCase() : i18n.t('Measures').toUpperCase(),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 90,
            callback(val) {
              if (this.ticks.length >= 40) {
                return;
              }
              return this.getLabelForValue(val);
            },
            display: true,
            autoSkip: false,
            barThickness: 30,
            font: {
              size: 12,
              family: baseFont(),
              weight: 500,
            },
            color: 'black',
          },
        },
        [otherAxis]: {
          title: {
            display: true,
            text: i18n.t('Score').toUpperCase(),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
          min: 0,
          max: 100,
          ticks: {
            stepSize: 10,
            callback: (val) => Number(val).toFixed(1),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
        },
      },
      plugins: {
        title: {
          display: false,
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            generateLabels(chart) {
              const { datasets } = chart.data;
              const {
                labels: { textAlign, color },
              } = chart.legend.options;

              const legend = chart._getSortedDatasetMetas().map((meta) => {
                const style = { ...meta.controller.getStyle(meta.index) };
                if (meta.index === 1) {
                  style.borderDash = [3, 3];
                  style.pointStyle = 'line';
                }
                if ((isMultipleSubjectSelected(subjects) && meta.type === 'line') || meta.type === 'bar') {
                  return null;
                }

                return {
                  text: meta.type === 'bar' ? i18n.t('Range Of Scores') : datasets[meta.index].label,
                  fillStyle: style.backgroundColor,
                  fontColor: color,
                  hidden: isMultipleSubjectSelected(subjects) && meta.type === 'line' ? false : !meta.visible,
                  lineCap: style.borderCapStyle,
                  lineDash: style.borderDash,
                  lineDashOffset: style.borderDashOffset,
                  lineJoin: style.borderJoinStyle,
                  lineWidth: 2,
                  strokeStyle: style.borderColor,
                  pointStyle: style.pointStyle,
                  rotation: style.rotation,
                  textAlign: textAlign || style.textAlign,
                  borderRadius: 0,
                  datasetIndex: meta.index,
                };
              }, this);

              if (groupHighlight) {
                const subjectColor = getSubjectColor({ state, subject: subjects[0] });
                legend.push({
                  text: getSelectedLocationName(state, groupHighlight.uid),
                  fillStyle: subjectColor,
                  fontColor: color,
                  hidden: false,
                  strokeStyle: datasets[0].borderColor,
                  pointStyle: 'circle',
                  borderRadius: 0,
                  datasetIndex: -1,
                });
              }

              return legend.filter((item) => item !== null);
            },
          },
          onClick: function (e, legendItem, legend) {
            const index = legendItem.datasetIndex;
            if (index === -1) {
              return false;
            }
            const ci = legend.chart;
            if (ci.isDatasetVisible(index)) {
              ci.hide(index);
              legendItem.hidden = true;
            } else {
              ci.show(index);
              legendItem.hidden = false;
            }
          },
        },
        tooltip: {
          enabled: false,
          external: (context) =>
            ChartjsTooltip({
              state,
              context,
              tooltipData,
              hideComparison: true,
              activeDatasets: [],
              chartName: 'dataPageBarChart',
              isMultiSubjectView: isMultipleSubjectSelected(subjects),
            }),
        },
      },
    },
  };
};
